import BackToCPQButton from "../../Components/backToCPQButton";
import PreviousPageButtonCPQ from "../../Components/PreviousPageButtonCPQ";

import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    Button,
    IconButton,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    Box,
    Grid,
    Snackbar,
    Alert,
    CircularProgress,
    InputAdornment,
    Tooltip,
    Divider,
    MenuItem,
} from "@mui/material";
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Search as SearchIcon,
    FilterAlt as FilterIcon,
    Clear as ClearIcon,
    ShowChart as ShowChartIcon,
    Description as DescriptionIcon,
} from "@mui/icons-material";
import apiClient from "../../Helpers/apiClient";
import PumpPerformanceEditor from "./PumpPerformanceEditor";
import DocumentAssociation from "../DocumentManagerPage/DocumentAssociation";

const INITIAL_FORM_DATA = {
    id: 0,
    model: "",
    supplierModel: "",
    useType: "",
    impellerType: "",
    description: "",
    pedestal: "",
    discharge: 0,
    voltage: 0,
    kw: 0,
    amps: 0,
    costPrice: 0,
    sellPrice: 0,
};

const NUMERIC_FIELDS = ["discharge", "voltage", "kw", "amps", "costPrice", "sellPrice"];

const SORTABLE_COLUMNS = [
    { id: "modelNo", label: "ID" },
    { id: "model", label: "Model" },
    { id: "supplierModel", label: "Supplier Model" },
    { id: "useType", label: "Use Type" },
    { id: "impellerType", label: "Impeller Type" },
    { id: "description", label: "Description" },
    { id: "pedestal", label: "Pedestal" },
    { id: "discharge", label: "Discharge" },
    { id: "voltage", label: "Voltage" },
    { id: "kw", label: "KW" },
    { id: "amps", label: "Amps" },
    { id: "costPrice", label: "Cost Price" },
    { id: "sellPrice", label: "Sell Price" },
];

function PumpPriceTable() {
    const [pumps, setPumps] = useState([]);
    const [filteredPumps, setFilteredPumps] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [pedestalOptions, setPedestalOptions] = useState([]);
    const [loadingPedestals, setLoadingPedestals] = useState(false);
    const [formData, setFormData] = useState(INITIAL_FORM_DATA);
    const [selectedPump, setSelectedPump] = useState(null);

    // Dialog states
    const [openDialog, setOpenDialog] = useState(false);
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [openPerformanceDialog, setOpenPerformanceDialog] = useState(false);
    const [documentDialogOpen, setDocumentDialogOpen] = useState(false);
    const [performancePumpId, setPerformancePumpId] = useState(null);

    // Sorting and filtering
    const [order, setOrder] = useState("asc");
    const [orderBy, setOrderBy] = useState("id");
    const [filterText, setFilterText] = useState("");
    const [filterField, setFilterField] = useState("all");

    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

    useEffect(() => {
        fetchPumps();
    }, []);

    useEffect(() => {
        applyFilters();
    }, [pumps, filterText, filterField]);

    const fetchPumps = async () => {
        setLoading(true);
        try {
            const response = await apiClient.get("/api/PumpStationPriceTables/PumpPrices");
            setPumps(Array.isArray(response.data) ? response.data : []);
            setError(null);
        } catch (err) {
            setError(`Failed to load data: ${err.message}`);
            setPumps([]);
        } finally {
            setLoading(false);
        }
    };

    const fetchPedestalOptions = async () => {
        setLoadingPedestals(true);
        try {
            const response = await apiClient.get("/api/PumpStationPriceTables/PedestalPrices");
            setPedestalOptions(Array.isArray(response.data) ? response.data : []);
        } catch (err) {
            setPedestalOptions([]);
        } finally {
            setLoadingPedestals(false);
        }
    };

    // Helper function to get property value (handles both camelCase and PascalCase)
    const getPropValue = (obj, propName) => {
        const camelCase = propName;
        const pascalCase = propName.charAt(0).toUpperCase() + propName.slice(1);
        return obj[camelCase] !== undefined ? obj[camelCase] : obj[pascalCase];
    };

    const handleOpenDialog = (pump = null) => {
        fetchPedestalOptions();
        if (pump) {
            setFormData(
                SORTABLE_COLUMNS.reduce(
                    (acc, col) => {
                        if (col.id !== "modelNo") {
                            acc[col.id] =
                                getPropValue(pump, col.id) || (NUMERIC_FIELDS.includes(col.id) ? 0 : "");
                        }
                        return acc;
                    },
                    { id: getPropValue(pump, "id") || 0 }
                )
            );
            setSelectedPump(pump);
        } else {
            setFormData(INITIAL_FORM_DATA);
            setSelectedPump(null);
        }
        setOpenDialog(true);
    };

    const handleFormChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: NUMERIC_FIELDS.includes(name) ? parseFloat(value) || 0 : value,
        }));
    };

    const handleSubmit = async () => {
        try {
            // Convert to PascalCase for API
            const payload = Object.keys(formData).reduce((acc, key) => {
                acc[key.charAt(0).toUpperCase() + key.slice(1)] = formData[key];
                return acc;
            }, {});

            const url = selectedPump
                ? `/api/PumpStationPriceTables/PumpPrices/${payload.Id}`
                : "/api/PumpStationPriceTables/PumpPrices";

            const response = selectedPump
                ? await apiClient.put(url, payload)
                : await apiClient.post(url, payload);

            setSnackbar({
                open: true,
                message: `Pump ${selectedPump ? "updated" : "created"} successfully!`,
                severity: "success",
            });

            if (!selectedPump && response.data?.id) {
                handleOpenPerformanceDialog(response.data.id);
            }

            setOpenDialog(false);
            fetchPumps();
        } catch (err) {
            setSnackbar({
                open: true,
                message: `Error: ${err.response?.data?.message || "Failed to save"}`,
                severity: "error",
            });
        }
    };

    const handleDelete = async () => {
        try {
            await apiClient.delete(
                `/api/PumpStationPriceTables/PumpPrices/${getPropValue(selectedPump, "id")}`
            );
            setSnackbar({ open: true, message: "Pump deleted successfully!", severity: "success" });
            setOpenDeleteDialog(false);
            fetchPumps();
        } catch (err) {
            setSnackbar({
                open: true,
                message: `Error: ${err.response?.data?.message || "Failed to delete"}`,
                severity: "error",
            });
        }
    };

    const handleRequestSort = (property) => {
        const isAsc = orderBy === property && order === "asc";
        setOrder(isAsc ? "desc" : "asc");
        setOrderBy(property);
    };

    const getSortedData = (data) => {
        if (!data.length) return [];
        return [...data].sort((a, b) => {
            let valueA, valueB;

            if (orderBy === "modelNo") {
                valueA = parseInt(getPropValue(a, "id") || 0);
                valueB = parseInt(getPropValue(b, "id") || 0);
            } else {
                valueA = getPropValue(a, orderBy) || (NUMERIC_FIELDS.includes(orderBy) ? 0 : "");
                valueB = getPropValue(b, orderBy) || (NUMERIC_FIELDS.includes(orderBy) ? 0 : "");
            }

            if (NUMERIC_FIELDS.includes(orderBy) || orderBy === "modelNo") {
                return order === "asc" ? valueA - valueB : valueB - valueA;
            }

            const strA = String(valueA).toLowerCase();
            const strB = String(valueB).toLowerCase();
            return order === "asc" ? strA.localeCompare(strB) : strB.localeCompare(strA);
        });
    };

    const applyFilters = () => {
        if (!filterText.trim()) {
            setFilteredPumps(pumps);
            return;
        }

        const searchTerm = filterText.toLowerCase();
        const filtered = pumps.filter((pump) => {
            const modelNo = `PM-${getPropValue(pump, "id").toString().padStart(3, "0")}`;

            if (filterField === "all") {
                return (
                    modelNo.toLowerCase().includes(searchTerm) ||
                    SORTABLE_COLUMNS.some((col) =>
                        String(getPropValue(pump, col.id) || "")
                            .toLowerCase()
                            .includes(searchTerm)
                    )
                );
            }

            if (filterField === "modelNo") {
                return modelNo.toLowerCase().includes(searchTerm);
            }

            return String(getPropValue(pump, filterField) || "")
                .toLowerCase()
                .includes(searchTerm);
        });

        setFilteredPumps(filtered);
    };

    const displayData = getSortedData(filteredPumps);

    const handleOpenPerformanceDialog = (pumpId) => {
        setPerformancePumpId(pumpId);
        setOpenPerformanceDialog(true);
    };

    const formatPrice = (price) => {
        return typeof price === "number" ? price.toFixed(2) : "0.00";
    };

    return (
        <Box
            sx={{
                height: "100vh",
                display: "flex",
                flexDirection: "column",
                padding: 4,
                paddingTop: 2,
                boxSizing: "border-box",
                overflow: "hidden",
            }}
        >
            <Typography variant="h4" gutterBottom align="center">
                Pump Price Table
            </Typography>

            <Paper
                sx={{
                    width: "100%",
                    flexGrow: 1,
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden",
                    mb: 2,
                }}
            >
                <Box sx={{ display: "flex", justifyContent: "space-between", p: 2, alignItems: "center" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: "60%" }}>
                        <TextField
                            variant="outlined"
                            placeholder="Search..."
                            size="small"
                            fullWidth
                            value={filterText}
                            onChange={(e) => setFilterText(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon />
                                    </InputAdornment>
                                ),
                                endAdornment: filterText ? (
                                    <InputAdornment position="end">
                                        <IconButton
                                            size="small"
                                            onClick={() => {
                                                setFilterText("");
                                                setFilterField("all");
                                            }}
                                        >
                                            <ClearIcon />
                                        </IconButton>
                                    </InputAdornment>
                                ) : null,
                            }}
                        />
                        <TextField
                            select
                            size="small"
                            value={filterField}
                            onChange={(e) => setFilterField(e.target.value)}
                            sx={{ minWidth: 120 }}
                        >
                            <MenuItem value="all">All Fields</MenuItem>
                            {SORTABLE_COLUMNS.map((col) => (
                                <MenuItem key={col.id} value={col.id}>
                                    {col.label}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Box>
                    <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>
                        Add New Pump
                    </Button>
                </Box>

                <Divider />

                {loading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                        <CircularProgress />
                    </Box>
                ) : error ? (
                    <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                        <Typography color="error">{error}</Typography>
                    </Box>
                ) : (
                    <>
                        <TableContainer sx={{ flex: 1, overflow: "auto" }}>
                            <Table stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        {SORTABLE_COLUMNS.map((col) => (
                                            <TableCell key={col.id}>
                                                <TableSortLabel
                                                    active={orderBy === col.id}
                                                    direction={orderBy === col.id ? order : "asc"}
                                                    onClick={() => handleRequestSort(col.id)}
                                                >
                                                    <strong>{col.label}</strong>
                                                </TableSortLabel>
                                            </TableCell>
                                        ))}
                                        <TableCell align="center">
                                            <strong>Actions</strong>
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {displayData.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={SORTABLE_COLUMNS.length + 1} align="center">
                                                {filterText ? "No matches found" : "No data available"}
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        displayData.map((pump) => (
                                            <TableRow key={getPropValue(pump, "id")} hover>
                                                <TableCell>
                                                    PM-{getPropValue(pump, "id").toString().padStart(3, "0")}
                                                </TableCell>
                                                {SORTABLE_COLUMNS.slice(1).map((col) => (
                                                    <TableCell key={col.id}>
                                                        {col.id === "costPrice" || col.id === "sellPrice"
                                                            ? `$${formatPrice(getPropValue(pump, col.id))}`
                                                            : getPropValue(pump, col.id) ||
                                                              (NUMERIC_FIELDS.includes(col.id) ? 0 : "")}
                                                    </TableCell>
                                                ))}
                                                <TableCell align="center">
                                                    <IconButton
                                                        color="primary"
                                                        onClick={() => handleOpenDialog(pump)}
                                                        size="small"
                                                    >
                                                        <EditIcon />
                                                    </IconButton>
                                                    <IconButton
                                                        color="secondary"
                                                        onClick={() =>
                                                            handleOpenPerformanceDialog(
                                                                getPropValue(pump, "id")
                                                            )
                                                        }
                                                        size="small"
                                                    >
                                                        <ShowChartIcon />
                                                    </IconButton>
                                                    <IconButton
                                                        color="secondary"
                                                        onClick={() => {
                                                            setSelectedPump(pump);
                                                            setDocumentDialogOpen(true);
                                                        }}
                                                        size="small"
                                                    >
                                                        <DescriptionIcon />
                                                    </IconButton>
                                                    <IconButton
                                                        color="error"
                                                        onClick={() => {
                                                            setSelectedPump(pump);
                                                            setOpenDeleteDialog(true);
                                                        }}
                                                        size="small"
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <Box
                            sx={{
                                p: 2,
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                            }}
                        >
                            <Typography variant="body2" color="text.secondary">
                                {filterText
                                    ? `Showing ${displayData.length} of ${pumps.length} items`
                                    : `Total: ${pumps.length} items`}
                            </Typography>
                        </Box>
                    </>
                )}
            </Paper>

            <Box sx={{ mt: 2, textAlign: "center", flexShrink: 0 }}>
                <Button variant="contained" component={Link} to="/pumpstation-price-tables">
                    Back to Pump Station Tables
                </Button>
            </Box>

            {/* Create/Edit Dialog */}
            <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
                <DialogTitle>{selectedPump ? "Edit Pump" : "Add New Pump"}</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Model"
                                name="model"
                                value={formData.model}
                                onChange={handleFormChange}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Supplier Model"
                                name="supplierModel"
                                value={formData.supplierModel}
                                onChange={handleFormChange}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Use Type"
                                name="useType"
                                value={formData.useType}
                                onChange={handleFormChange}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Impeller Type"
                                name="impellerType"
                                value={formData.impellerType}
                                onChange={handleFormChange}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Description"
                                name="description"
                                value={formData.description}
                                onChange={handleFormChange}
                                multiline
                                rows={2}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                select
                                fullWidth
                                label="Pedestal"
                                name="pedestal"
                                value={formData.pedestal}
                                onChange={handleFormChange}
                                disabled={loadingPedestals}
                            >
                                <MenuItem value="">None</MenuItem>
                                {pedestalOptions.map((option) => (
                                    <MenuItem key={option.id} value={option.pedestal}>
                                        {option.pedestal}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Discharge"
                                name="discharge"
                                type="number"
                                value={formData.discharge}
                                onChange={handleFormChange}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                fullWidth
                                label="Voltage"
                                name="voltage"
                                type="number"
                                value={formData.voltage}
                                onChange={handleFormChange}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                fullWidth
                                label="KW"
                                name="kw"
                                type="number"
                                value={formData.kw}
                                onChange={handleFormChange}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                fullWidth
                                label="Amps"
                                name="amps"
                                type="number"
                                value={formData.amps}
                                onChange={handleFormChange}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Cost Price"
                                name="costPrice"
                                type="number"
                                value={formData.costPrice}
                                onChange={handleFormChange}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Sell Price"
                                name="sellPrice"
                                type="number"
                                value={formData.sellPrice}
                                onChange={handleFormChange}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
                    <Button onClick={handleSubmit} variant="contained">
                        {selectedPump ? "Update" : "Save"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Dialog */}
            <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
                <DialogTitle>Confirm Delete</DialogTitle>
                <DialogContent>
                    Are you sure you want to delete this pump? This action cannot be undone.
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDeleteDialog(false)}>Cancel</Button>
                    <Button onClick={handleDelete} color="error" variant="contained">
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Performance Dialog */}
            <Dialog
                open={openPerformanceDialog}
                onClose={() => setOpenPerformanceDialog(false)}
                maxWidth="lg"
                fullWidth
            >
                <DialogTitle>
                    Pump Performance Data{" "}
                    {performancePumpId ? `- PM-${performancePumpId.toString().padStart(3, "0")}` : ""}
                </DialogTitle>
                <DialogContent>
                    {performancePumpId && (
                        <PumpPerformanceEditor
                            pumpId={performancePumpId}
                            onClose={() => setOpenPerformanceDialog(false)}
                        />
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenPerformanceDialog(false)}>Close</Button>
                </DialogActions>
            </Dialog>

            {/* Document Dialog */}
            <Dialog
                open={documentDialogOpen}
                onClose={() => setDocumentDialogOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    Document Management{" "}
                    {selectedPump
                        ? `- PM-${getPropValue(selectedPump, "id").toString().padStart(3, "0")}`
                        : ""}
                </DialogTitle>
                <DialogContent>
                    {selectedPump && (
                        <DocumentAssociation
                            entityType="PumpPrice"
                            entityId={getPropValue(selectedPump, "id")}
                            entityName={`Pump ${getPropValue(selectedPump, "model") || ""}`}
                        />
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDocumentDialogOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    severity={snackbar.severity}
                    variant="filled"
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}

export default PumpPriceTable;
